/*
  Page document enhancement.

  The five footer-linked pages (About us, Privacy, Terms, Shipping & delivery,
  FAQ) were all pasted in through TinyMCE, and four of the five carry NO real
  headings at all. Their section headings are `<p><strong>…</strong></p>` —
  a paragraph whose entire content is one bold run. The FAQ is the mirror image:
  it has real <h2>/<h3> for sections, and each Q&A is
  `<p><strong>Question</strong> answer…</p>`, question and answer in one
  paragraph.

  So the structure is real, it is just not marked up. This script reads it and
  adds classes; assets are styled in the section's own <style> block. Nothing is
  restructured and no node is moved, so with JS off the page still reads exactly
  as the merchant typed it — bold lead-ins and all — it simply loses the
  typographic hierarchy and the jump list.

  Why a length guard rather than "every bold paragraph is a heading": About us
  ends on a 202-character rallying paragraph that is entirely bold, and setting
  that in uppercase Bebas would be a disaster. Short bold paragraph = heading,
  long bold paragraph = lede.
*/
(function () {
  'use strict';

  var root = document.querySelector('[data-ef-doc-body]');
  if (!root) return;

  var host = root.closest('[data-ef-doc]') || root;
  var HEADING_MAX = parseInt(host.getAttribute('data-heading-max'), 10);
  var TOC_MIN = parseInt(host.getAttribute('data-toc-min'), 10);
  var pageTitle = host.getAttribute('data-page-title') || '';
  if (isNaN(HEADING_MAX)) HEADING_MAX = 56;
  if (isNaN(TOC_MIN)) TOC_MIN = 5;

  function norm(s) {
    return String(s)
      .toLowerCase()
      .replace(/[‘’']/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function tidy(s) {
    return String(s).replace(/ /g, ' ').replace(/\s+/g, ' ').trim();
  }

  var headings = [];

  /* One ordered pass over the body's own children, so the jump list comes out
     in document order without a second query. */
  Array.prototype.forEach.call(root.children, function (el) {
    var tag = el.tagName;

    if (tag === 'H1' || tag === 'H2' || tag === 'H3' || tag === 'H4') {
      el.classList.add('ef-doc__h');
      headings.push(el);
      return;
    }

    if (tag !== 'P') return;

    /* TinyMCE leaves `<p>&nbsp;</p>` spacers behind. They are not :empty —
       a non-breaking space is a real text node — so CSS alone cannot find them. */
    if (!tidy(el.textContent) && !el.querySelector('img, iframe, video, br')) {
      el.classList.add('ef-doc__blank');
      return;
    }

    var strongs = [];
    Array.prototype.forEach.call(el.children, function (c) {
      if (c.tagName === 'STRONG' || c.tagName === 'B') strongs.push(c);
    });
    if (strongs.length !== 1) return;

    var strong = strongs[0];
    var label = tidy(strong.textContent);
    if (!label) return;

    /* Everything in the paragraph that is not the bold run, split by whether it
       falls before or after it. Walking childNodes rather than diffing
       textContent, because a naive string subtraction breaks when the bold text
       also occurs in the body of the sentence — and `firstElementChild` is not
       good enough for "does the paragraph OPEN with the bold run", because it
       skips text nodes. Privacy ends on "…contact us at <strong>support@…
       </strong>.", which that test read as a question. */
    var before = '';
    var after = '';
    var passed = false;
    Array.prototype.forEach.call(el.childNodes, function (n) {
      if (n === strong) { passed = true; return; }
      if (passed) { after += n.textContent; } else { before += n.textContent; }
    });
    before = tidy(before);
    after = tidy(after);
    var rest = tidy(before + ' ' + after);

    if (!rest) {
      if (label.length <= HEADING_MAX) {
        el.classList.add('ef-doc__h', 'ef-doc__h--promoted');
        el.setAttribute('role', 'heading');
        el.setAttribute('aria-level', '2');
        headings.push(el);
      } else {
        el.classList.add('ef-doc__lede');
      }
    } else if (!before && after.length >= 40 && label.length <= 120) {
      /* "<strong>Question</strong> answer…" — the FAQ pattern. Requires the bold
         run to genuinely open the paragraph and to be followed by real prose.
         The length floor is what separates a question from a `Label: value`
         line: FAQ answers run 105–178 characters, while the return policy's
         "<strong>Effective Date:</strong> 01 Jan, 2025" trails just 12, and
         breaking that onto two lines reads as a fault. */
      el.classList.add('ef-doc__qa');
      strong.classList.add('ef-doc__q');
    }
  });

  /* All five pages restate their own title as the first line of the body —
     "Elite Fishin' Shop Privacy Policy" under an h1 reading "Privacy Policy".
     Hide it rather than delete it: the copy stays untouched in the admin. */
  if (headings.length && pageTitle) {
    var t = norm(pageTitle);
    var first = norm(headings[0].textContent);
    if (t && first && (first.indexOf(t) !== -1 || t.indexOf(first) !== -1)) {
      headings[0].classList.add('ef-doc__h--dupe');
      headings[0].setAttribute('aria-hidden', 'true');
      headings.shift();
    }
  }

  if (headings.length < TOC_MIN) return;

  var seen = {};
  var nav = document.createElement('nav');
  nav.className = 'ef-doc__toc';
  nav.setAttribute('aria-label', 'On this page');

  /* A <details> so the list can collapse on a narrow screen. Shipping &
     delivery runs 22 entries — open, that is a screen and a half of navigation
     before the first word of the page. On desktop it is opened below and the
     summary reads as the plain caption it was. */
  var det = document.createElement('details');
  det.className = 'ef-doc__toc-det';

  var caption = document.createElement('summary');
  caption.className = 'ef-doc__toc-title';
  caption.textContent = 'On this page';
  det.appendChild(caption);

  var list = document.createElement('ul');
  list.className = 'ef-doc__toc-list';

  headings.forEach(function (el) {
    /* Shipping & delivery ends every heading with a colon — fine in place,
       noise in a jump list. */
    var text = tidy(el.textContent).replace(/[:–—\-\s]+$/, '');
    if (!text) return;

    var id = el.id;
    if (!id) {
      var base = 'sec-' + (norm(text).replace(/\s+/g, '-').slice(0, 60) || 'section');
      id = base;
      var n = 2;
      while (seen[id] || document.getElementById(id)) id = base + '-' + n++;
      el.id = id;
    }
    seen[id] = true;

    var li = document.createElement('li');
    var a = document.createElement('a');
    a.href = '#' + id;
    a.textContent = text;
    li.appendChild(a);
    list.appendChild(li);
  });

  if (!list.children.length) return;
  det.appendChild(list);
  nav.appendChild(det);

  var wide = window.matchMedia('(min-width: 750px)');
  var short = list.children.length <= 6;
  function syncOpen() { det.open = short || wide.matches; }
  syncOpen();
  if (wide.addEventListener) wide.addEventListener('change', syncOpen);
  else if (wide.addListener) wide.addListener(syncOpen);

  root.parentNode.insertBefore(nav, root);
})();
